import { useDispatch, useSelector } from "react-redux";
import { selectIsCrmLabelEnabled } from "app/pages/sales-intelligence/sub-modules/management/store/selectors/crmSettingsSelectors";
import { setIsSfCrmLabelEnabledThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforce/toggles";
import { setIsHsCrmLabelEnabledThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/toggles";
import { setIsDynamicsCrmLabelEnabledThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/toggles";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import type { ThunkActionCommon } from "single-spa/store/types";
import {
    selectHsExcludeAccountFields,
    selectHsIsLoadingExcludeAccountFields,
    selectHsCurrentStatusField,
    selectIsUpdatingHsStatusField,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/hubspotSelectors";
import {
    selectSfExcludeAccountFields,
    selectSfIsLoadingExcludeAccountFields,
    selectSfCurrentStatusField,
    selectIsUpdatingSfStatusField,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/salesforceSelectors";
import {
    selectDynamicsExcludeAccountFields,
    selectDynamicsIsLoadingExcludeAccountFields,
    selectDynamicsCurrentStatusField,
    selectIsUpdatingDynamicsStatusField,
} from "app/pages/sales-intelligence/sub-modules/management/store/selectors/dynamicsSelectors";
import { putSfStatusFieldThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/salesforceApi";
import { putHsStatusFieldThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/hubspot/exclude";
import { putDynamicsStatusFieldThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/dynamics/exclude";

const setIsCrmLabelEnabledThunkConfig: Record<Integration, (payload: boolean) => ThunkActionCommon> = {
    salesforce: setIsSfCrmLabelEnabledThunk,
    hubspot: setIsHsCrmLabelEnabledThunk,
    dynamics: setIsDynamicsCrmLabelEnabledThunk,
};

const setStatusFieldThunkConfig: Record<Integration, (payload: string) => ThunkActionCommon> = {
    salesforce: putSfStatusFieldThunk,
    hubspot: putHsStatusFieldThunk,
    dynamics: putDynamicsStatusFieldThunk,
};

const propsByIntegration = {
    hubspot: {
        excludeAccountFields: selectHsExcludeAccountFields,
        isLoadingExcludeAccountFields: selectHsIsLoadingExcludeAccountFields,
        currentStatusField: selectHsCurrentStatusField,
        isUpdatingStatusField: selectIsUpdatingHsStatusField,
    },
    salesforce: {
        excludeAccountFields: selectSfExcludeAccountFields,
        isLoadingExcludeAccountFields: selectSfIsLoadingExcludeAccountFields,
        currentStatusField: selectSfCurrentStatusField,
        isUpdatingStatusField: selectIsUpdatingSfStatusField,
    },
    dynamics: {
        excludeAccountFields: selectDynamicsExcludeAccountFields,
        isLoadingExcludeAccountFields: selectDynamicsIsLoadingExcludeAccountFields,
        currentStatusField: selectDynamicsCurrentStatusField,
        isUpdatingStatusField: selectIsUpdatingDynamicsStatusField,
    },
};

export const useAccountStatusProps = (
    crm: Integration,
): {
    isCrmLabelEnabled: boolean;
    setIsCrmLabelEnabled: (isEnabled: boolean) => void;
    crmFields: { label: string; value: string }[];
    isLoadingStatusField: boolean;
    statusField: string | undefined;
    setStatusField: (field: string) => void;
} => {
    const dispatch = useDispatch();

    const isCrmLabelEnabled = useSelector((state) => selectIsCrmLabelEnabled(state, crm));
    const crmFields = useSelector(propsByIntegration[crm].excludeAccountFields).map((field) => ({
        value: field.name,
        label: field.label,
    }));
    const isLoadingCrmFields = useSelector(propsByIntegration[crm].isLoadingExcludeAccountFields);
    const statusField = useSelector(propsByIntegration[crm].currentStatusField);
    const isUpdatingStatusField = useSelector(propsByIntegration[crm].isUpdatingStatusField);

    const setIsCrmLabelEnabled = (isEnabled: boolean) => dispatch(setIsCrmLabelEnabledThunkConfig[crm](isEnabled));

    const setStatusField = (field: string) => dispatch(setStatusFieldThunkConfig[crm](field));

    return {
        isCrmLabelEnabled,
        setIsCrmLabelEnabled,
        crmFields,
        isLoadingStatusField: isLoadingCrmFields || isUpdatingStatusField,
        statusField,
        setStatusField,
    };
};
